import { useCallback, useEffect, useMemo, useState } from "react";
import { SURGICAL_ACT_LIMITS } from "../../../classes/constants/surgicalActConstants";
import { useNotification } from "../../../classes/contexts/NotificationContext";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { EventsEnum } from "../../../classes/enums/EventsEnum";
import { MedicalImagesType } from "../../../classes/enums/MedicalImagesEnum";
import { SnackbarType } from "../../../classes/enums/SnackbarTypeEnum";
import {
  ActNatures,
  SegementalTransformationTypes,
} from "../../../classes/enums/SurgicalActsEnum";
import { VertebraSelectionModality } from "../../../classes/enums/VertebraSelectionEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import Button from "../../../components/button/Button";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import LabeledSelect from "../../../components/select/LabeledSelect";
import LabeledSliderDiscrete from "../../../components/select/LabeledSliderDiscrete";
import useSurgicalActActions from "../../../hooks/useSurgicalActActions";
import "./AlignPopupParameterSelector.css";

const Cage = ({
  isEditMode = false,
  existingAct = null,
  onConfirm = null,
  onRequiredFieldsChange = null,
  hasL6 = false,
  hasT13 = false,
}) => {
  const { currentPlanId } = usePlanContext();
  const { addNotification } = useNotification();

  const {
    startVertebraSelection,
    stopVertebraSelection,
    showCageTool,
    removeCagePreview,
    getCageAttributes,
    rotateCageTool,
    setHeightCageTool,
    setWidthCageTool,
    setCagePosition,
  } = useSurgicalActActions(currentPlanId);

  // Tool UI state
  const [selectedVertebra, setSelectedVertebra] = useState(null);
  const [belowCageVertebraType, setBelowCageVertebraType] = useState(null);
  const [rotationValue, setRotationValue] = useState(0);
  const [cageHeight, setCageHeight] = useState(10);
  const [cageWidth, setCageWidth] = useState(10);
  const [cagePosition, setCagePositionState] = useState(0);
  const [maxWidth, setMaxWidth] = useState(50);
  const [minHeight, setMinHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);

  // State to track flow
  const [isVertebraSelectionActive, setIsVertebraSelectionActive] =
    useState(false);
  const [isCageToolActive, setIsCageToolActive] = useState(false);
  const [isLoadingCageTool, setIsLoadingCageTool] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const MIN_ROTATION = SURGICAL_ACT_LIMITS.CAGE.ANGLE.min;
  const MAX_ROTATION = SURGICAL_ACT_LIMITS.CAGE.ANGLE.max;

  const cageDiskItems = useMemo(
    () => vertebraUtils.getCageDiskItems(hasL6, hasT13),
    [hasL6, hasT13]
  );

  const vertebraToDiskMap = useMemo(() => {
    return cageDiskItems.reduce((map, item) => {
      const [vertebra] = item.split("-");
      map[vertebra] = item;
      return map;
    }, {});
  }, [cageDiskItems]);

  const diskToVertebraMap = useMemo(() => {
    return cageDiskItems.reduce((map, item) => {
      const [vertebra] = item.split("-");
      map[item] = vertebra;
      return map;
    }, {});
  }, [cageDiskItems]);

  // Helper function to get disk for vertebra
  const getDiskForVertebra = useCallback(
    vertebra => {
      if (!vertebra || vertebra === "-") return null;
      return vertebraToDiskMap[vertebra] ?? null;
    },
    [vertebraToDiskMap]
  );

  // Helper function to get vertebra from disk
  const getVertebraFromDisk = useCallback(
    disk => {
      if (!disk || disk === "-") return null;
      return diskToVertebraMap[disk] ?? disk;
    },
    [diskToVertebraMap]
  );

  // region CONFIG
  const vertebraItems = useMemo(() => {
    const items = ["-", ...cageDiskItems];

    return items.map(item => ({
      value: item,
      label: item === "-" ? "-" : item,
    }));
  }, [cageDiskItems]);

  const lengthItems = [
    { value: 10, label: "10 mm" },
    { value: 20, label: "20 mm" },
  ];

  const positionOptions = [
    { value: 0, label: "A" }, // Anterior
    { value: 0.5, label: "M" }, // Middle
    { value: 1, label: "P" }, // Posterior
  ];

  const selectedVertebraItem = useMemo(() => {
    if (!selectedVertebra || selectedVertebra === "-") {
      return vertebraItems[0];
    }

    // Get the disk for the selected vertebra for display
    const diskForDisplay = getDiskForVertebra(selectedVertebra);

    const foundItem = vertebraItems.find(item => item.value === diskForDisplay);

    return foundItem || vertebraItems[0];
  }, [selectedVertebra, vertebraItems, getDiskForVertebra]);

  const getVertebraId = useCallback(vertebra => {
    if (!vertebra || vertebra === "-") return null;
    return vertebraUtils.getCentroidEntity(vertebra);
  }, []);

  const isConfirmDisabled = useMemo(() => {
    const hasValidVertebra = selectedVertebra && selectedVertebra !== "-";
    const hasValidRotation =
      rotationValue >= MIN_ROTATION && rotationValue <= MAX_ROTATION;
    const hasValidHeight = cageHeight >= 0;
    const hasValidWidth = cageWidth > 0 && cageWidth <= maxWidth;

    return (
      !hasValidVertebra ||
      !hasValidRotation ||
      !hasValidHeight ||
      !hasValidWidth ||
      isLoadingCageTool ||
      isConfirming
    );
  }, [
    selectedVertebra,
    rotationValue,
    cageHeight,
    cageWidth,
    maxWidth,
    MIN_ROTATION,
    MAX_ROTATION,
    isLoadingCageTool,
    isConfirming,
  ]);

  const getAllVertebrae = useCallback(() => {
    return vertebraUtils.getAvailableVertebrae(hasL6, hasT13);
  }, [hasL6, hasT13]);

  // endregion

  // region SETUP
  useEffect(() => {
    startVertebraSelection(
      MedicalImagesType.LATERAL_SURGERY_DX,
      VertebraSelectionModality.CAGE,
      1
    );
    setIsVertebraSelectionActive(true);
  }, [startVertebraSelection]);

  // Check if required fields are filled and notify parent
  useEffect(() => {
    if (onRequiredFieldsChange) {
      const hasValidVertebra = selectedVertebra && selectedVertebra !== "-";
      onRequiredFieldsChange("CAGE", hasValidVertebra);
    }
  }, [selectedVertebra, onRequiredFieldsChange]);

  useEffect(() => {
    if (isEditMode && existingAct) {
      // Set the vertebra
      if (existingAct.vertebra) {
        setSelectedVertebra(existingAct.vertebra);
      }

      // Set the vertebra below
      if (existingAct.vertebraBelow) {
        setBelowCageVertebraType(existingAct.vertebraBelow);
      }

      // Set cage parameters
      if (existingAct.rotation !== undefined) {
        setRotationValue(existingAct.rotation);
      }

      if (existingAct.cageHeight !== undefined) {
        setCageHeight(existingAct.cageHeight);
      }

      if (existingAct.cageWidth !== undefined) {
        setCageWidth(existingAct.cageWidth);
      }

      if (existingAct.cagePosition !== undefined) {
        setCagePositionState(existingAct.cagePosition);
      }

      if (existingAct.maxWidth !== undefined) {
        setMaxWidth(existingAct.maxWidth);
      }

      if (existingAct.minHeight !== undefined) {
        setMinHeight(existingAct.minHeight);
      }

      if (existingAct.maxHeight !== undefined) {
        setMaxHeight(existingAct.maxHeight);
      }

      // Stop vertebra selection since we're editing
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }
    }
  }, [
    isEditMode,
    existingAct,
    isVertebraSelectionActive,
    stopVertebraSelection,
  ]);

  // region CLEANUP
  useEffect(() => {
    return () => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
      }
      if (isCageToolActive) {
        removeCagePreview();
      }
    };
  }, [
    stopVertebraSelection,
    isVertebraSelectionActive,
    isCageToolActive,
    removeCagePreview,
  ]);
  // region CALLBACKS
  const handleVertebraSelected = useCallback(
    event => {
      const tag = event.detail[0].tag;
      if (!tag || tag !== VertebraSelectionModality.CAGE) {
        return;
      }

      const vertebraId = event.detail[0].id;
      const vertebraType = vertebraUtils.getVertebraFromId(vertebraId);

      // Store the actual vertebra (this goes to backend)
      setSelectedVertebra(vertebraType);

      // Auto-set the vertebra below from the disk name (e.g. "L5-S1" → "SACRUM")
      const disk = vertebraToDiskMap[vertebraType];
      const below = disk ? disk.split("-")[1] ?? null : null;
      setBelowCageVertebraType(
        below ? vertebraUtils._normalizeSacrumLabel(below) : null
      );
    },
    [vertebraToDiskMap]
  );

  const handleManualVertebraChange = useCallback(
    selectedItem => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }

      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;

      // Convert disk selection back to vertebra for backend
      const actualVertebra = getVertebraFromDisk(value);
      setSelectedVertebra(actualVertebra === "-" ? null : actualVertebra);

      // Auto-set the vertebra below from the disk name (e.g. "L5-S1" → "SACRUM")
      if (actualVertebra && actualVertebra !== "-") {
        const below = value.split("-")[1] ?? null;
        setBelowCageVertebraType(
          below ? vertebraUtils._normalizeSacrumLabel(below) : null
        );
      } else {
        setBelowCageVertebraType(null);
      }
    },
    [
      isVertebraSelectionActive,
      getVertebraFromDisk,
      stopVertebraSelection,
    ]
  );

  const showCageErrorNotification = useCallback(
    (errorPayload, fallbackMessage = "Failed to update cage.") => {
      if (!errorPayload || errorPayload.isUserNotified) {
        return;
      }

      const message = errorPayload.message || fallbackMessage;

      addNotification(SnackbarType.WARNING, message, {
        title: "Cage",
        duration: 0,
      });
    },
    [addNotification]
  );

  // Rotation handlers
  const applyRotationDelta = useCallback(
    async delta => {
      const numericDelta = Number(delta);
      if (!Number.isFinite(numericDelta) || numericDelta === 0) return;

      try {
        const response = await rotateCageTool(numericDelta);
        if (response && response.cageParameters) {
          setRotationValue(
            prev => response.cageParameters.angle ?? prev + numericDelta
          );
        } else {
          setRotationValue(prev => prev + numericDelta);
        }
      } catch (error) {
        showCageErrorNotification(error);
      }
    },
    [rotateCageTool, showCageErrorNotification]
  );

  // Height handlers
  const handleHeightChange = useCallback(
    async targetHeight => {
      const clampedTarget = Math.max(Number(targetHeight), 0);
      const delta = clampedTarget - cageHeight;
      if (delta === 0) return;

      try {
        const response = await setHeightCageTool(delta);
        if (response && response.cageParameters) {
          setCageHeight(response.cageParameters.cageHeight || clampedTarget);
        } else {
          setCageHeight(clampedTarget);
        }
      } catch (error) {
        showCageErrorNotification(error);
      }
    },
    [cageHeight, setHeightCageTool, showCageErrorNotification]
  );

  // Width handlers
  const handleWidthChange = useCallback(
    async targetWidth => {
      const clampedTarget = Math.min(
        Math.max(Number(targetWidth), 1),
        maxWidth
      );

      try {
        const response = await setWidthCageTool(clampedTarget);
        if (response && response.cageParameters) {
          setCageWidth(response.cageParameters.cageWidth || clampedTarget);
          setMaxWidth(response.cageParameters.maxWidth || maxWidth);
        } else {
          setCageWidth(clampedTarget);
        }
      } catch (error) {
        showCageErrorNotification(error);
      }
    },
    [maxWidth, setWidthCageTool, showCageErrorNotification]
  );

  // Position handlers
  const handlePositionSnap = useCallback(
    async (value, label) => {
      try {
        const response = await setCagePosition(value);
        if (response && response.cageParameters) {
          setCagePositionState(response.cageParameters.cagePosition || value);
        } else {
          setCagePositionState(value);
        }
      } catch (error) {
        showCageErrorNotification(error);
      }
    },
    [setCagePosition, showCageErrorNotification]
  );

  // Increment and decrement functions
  const incrementRotation = useCallback(
    () => applyRotationDelta(1),
    [applyRotationDelta]
  );
  const decrementRotation = useCallback(
    () => applyRotationDelta(-1),
    [applyRotationDelta]
  );

  const incrementHeight = useCallback(
    () => handleHeightChange(cageHeight + 1),
    [cageHeight, handleHeightChange]
  );
  const decrementHeight = useCallback(
    () => handleHeightChange(cageHeight - 1),
    [cageHeight, handleHeightChange]
  );

  const incrementWidth = useCallback(
    () => handleWidthChange(cageWidth + 1),
    [cageWidth, handleWidthChange]
  );
  const decrementWidth = useCallback(
    () => handleWidthChange(cageWidth - 1),
    [cageWidth, handleWidthChange]
  );

  const handleManualCageToolUpdate = useCallback(
    event => {
      const updateData = event.detail[0];
      if (!updateData) {
        return;
      }

      if (updateData.error) {
        showCageErrorNotification(updateData.error);
        return;
      }

      // The cage parameters are nested under cageParameters property
      const cageParams = updateData.cageParameters;
      if (!cageParams) {
        return;
      }

      // Update rotation/angle
      if (cageParams.angle !== undefined) {
        const newRotation = Math.round(cageParams.angle);
        setRotationValue(newRotation);
      }

      // Update cage height
      if (cageParams.cageHeight !== undefined) {
        const newHeight = Math.round(cageParams.cageHeight);
        setCageHeight(newHeight);
      }

      // Update cage width
      if (cageParams.cageWidth !== undefined) {
        const newWidth = Math.round(cageParams.cageWidth);
        setCageWidth(newWidth);
      }

      // Update cage position
      if (cageParams.cagePosition !== undefined) {
        setCagePositionState(cageParams.cagePosition);
      }

      // Update max width
      if (cageParams.maxWidth !== undefined) {
        const newMaxWidth = Math.round(cageParams.maxWidth);
        setMaxWidth(newMaxWidth);
      }

      // Update min height
      if (cageParams.minHeight !== undefined) {
        const newMinHeight = Math.round(cageParams.minHeight);
        setMinHeight(newMinHeight);
      }

      // Update max height
      if (cageParams.maxHeight !== undefined) {
        const newMaxHeight = Math.round(cageParams.maxHeight);
        setMaxHeight(newMaxHeight);
      }

      // Also handle belowVertebra if it's provided
      if (cageParams.belowVertebra !== undefined) {
        // Convert vertebra ID back to vertebra type
        const belowVertebraType = vertebraUtils.getVertebraFromId(
          cageParams.belowVertebra
        );
        if (belowVertebraType) {
          setBelowCageVertebraType(belowVertebraType);
        }
      }
    },
    [showCageErrorNotification]
  );

  const handleConfirm = useCallback(async () => {
    // Prevent multiple confirmations
    if (isConfirming) return;

    // Validate all required fields
    if (!selectedVertebra || selectedVertebra === "-") {
      return;
    }

    setIsConfirming(true);

    try {
      // Get the vertebra ID for support values
      const vertebraId = getVertebraId(selectedVertebra);

      // Get support values at confirmation time
      let supportValues = null;
      if (vertebraId) {
        try {
          const cageAttributes = await getCageAttributes(vertebraId);
          if (cageAttributes && cageAttributes.supportPoints) {
            supportValues = cageAttributes.supportPoints;
          }
        } catch (error) {}
      }

      // Create the cage act with the actual vertebra (not the disk)
      const cageAct = {
        actNature: ActNatures.Cage,
        vertebra: selectedVertebra,
        vertebraBelow: belowCageVertebraType,
        value: null,
        type: SegementalTransformationTypes.FULL,
        rotation: rotationValue,
        cageHeight: cageHeight,
        cageWidth: cageWidth,
        cagePosition: cagePosition,
        maxWidth: maxWidth,
        minHeight: minHeight,
        maxHeight: maxHeight,
        supportPoints: supportValues,
      };

      if (onConfirm) {
        onConfirm(cageAct);
      } else {
      }
    } catch (error) {
    } finally {
      setIsConfirming(false);
    }
  }, [
    isConfirming,
    selectedVertebra,
    getVertebraId,
    belowCageVertebraType,
    rotationValue,
    cageHeight,
    cageWidth,
    cagePosition,
    maxWidth,
    minHeight,
    maxHeight,
    onConfirm,
    getCageAttributes,
  ]);

  // region EVENT LISTENERS
  useEffect(() => {
    window.addEventListener(
      EventsEnum.VERTEBRA_SELECTION,
      handleVertebraSelected
    );
    return () => {
      window.removeEventListener(
        EventsEnum.VERTEBRA_SELECTION,
        handleVertebraSelected
      );
    };
  }, [handleVertebraSelected]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.MANUAL_CAGE_TOOL_UPDATE,
      handleManualCageToolUpdate
    );
    return () => {
      window.removeEventListener(
        EventsEnum.MANUAL_CAGE_TOOL_UPDATE,
        handleManualCageToolUpdate
      );
    };
  }, [handleManualCageToolUpdate]);

  useEffect(() => {
    let timeoutId = null;

    if (selectedVertebra && selectedVertebra !== "-") {
      const vertebraId = getVertebraId(selectedVertebra);

      if (vertebraId) {
        timeoutId = setTimeout(async () => {
          setIsLoadingCageTool(true);

          try {
            const cageParameters = await showCageTool(
              vertebraId,
              selectedVertebra
            );

            if (cageParameters && cageParameters.cageParameters) {
              setRotationValue(
                Math.round(cageParameters.cageParameters.angle ?? 0)
              );
              setCageHeight(
                Math.round(cageParameters.cageParameters.cageHeight || 10)
              );
              setCageWidth(
                Math.round(cageParameters.cageParameters.cageWidth || 10)
              );
              setCagePositionState(
                cageParameters.cageParameters.cagePosition || 0
              );
              setMaxWidth(
                Math.round(cageParameters.cageParameters.maxWidth || 50)
              );
              setMinHeight(
                Math.round(cageParameters.cageParameters.minHeight || 0)
              );
              setMaxHeight(
                Math.round(cageParameters.cageParameters.maxHeight || 0)
              );
            }

            setIsCageToolActive(true);
          } catch (error) {
            showCageErrorNotification(error, "Failed to load cage tool.");
          } finally {
            setIsLoadingCageTool(false);
          }
        }, 100);
      }
    } else {
      if (isCageToolActive) {
        removeCagePreview();
        setIsCageToolActive(false);
        setIsLoadingCageTool(false);
        setRotationValue(0);
        setCageHeight(10);
        setCageWidth(10);
        setCagePositionState(0);
        setMaxWidth(50);
        setSelectedVertebra(null);
        setBelowCageVertebraType(null);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    selectedVertebra,
    getVertebraId,
    showCageTool,
    isCageToolActive,
    removeCagePreview,
    showCageErrorNotification,
  ]);

  // region RENDERING
  return (
    <div className="AlignPopupParameterSelector-container">
      <div className="AlignPopupParameterSelector-scrollable-content">
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <div className="AlignPopupParameterSelector-header">
            {(!selectedVertebra || selectedVertebra === "-") &&
              isVertebraSelectionActive && (
                <div className="App-text-medium AlignPopupParameterSelector-subtitle">
                  Select a point on the image
                </div>
              )}
          </div>

          <LabeledSelect
            label="Vertebra"
            items={vertebraItems}
            selectedItem={selectedVertebraItem}
            onChange={handleManualVertebraChange}
          />

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label App-text-large ${
                isLoadingCageTool || isConfirming || !isCageToolActive
                  ? "disabled"
                  : ""
              }`}
            >
              Angle
            </div>
            <MeasurementDisplay
              value={rotationValue}
              unit="°"
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
            <IncrementDecrementButtons
              onIncrement={incrementRotation}
              onDecrement={decrementRotation}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label ${
                isLoadingCageTool || isConfirming || !isCageToolActive
                  ? "disabled"
                  : ""
              } App-text-large`}
            >
              Height
            </div>
            <MeasurementDisplay
              value={cageHeight}
              unit="mm"
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
            <IncrementDecrementButtons
              onIncrement={incrementHeight}
              onDecrement={decrementHeight}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label  ${
                isLoadingCageTool || isConfirming || !isCageToolActive
                  ? "disabled"
                  : ""
              } `}
            >
              Width
            </div>
            <MeasurementDisplay
              value={cageWidth}
              unit="mm"
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
            <IncrementDecrementButtons
              onIncrement={incrementWidth}
              onDecrement={decrementWidth}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
            />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label ${
                isLoadingCageTool || isConfirming || !isCageToolActive
                  ? "disabled"
                  : ""
              } App-text-large`}
            >
              Min Height
            </div>
            <MeasurementDisplay value={minHeight} unit="mm" disabled={true} />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label ${
                isLoadingCageTool || isConfirming || !isCageToolActive
                  ? "disabled"
                  : ""
              } App-text-large`}
            >
              Max Height
            </div>
            <MeasurementDisplay value={maxHeight} unit="mm" disabled={true} />
          </div>

          <LabeledSelect
            label="Length"
            items={lengthItems}
            selectedItem={lengthItems[0]}
            onChange={() => {}}
          />

          <LabeledSliderDiscrete
            label="Position"
            options={positionOptions}
            value={cagePosition}
            onChange={() => {}}
            onSnap={handlePositionSnap}
            disabled={isLoadingCageTool || isConfirming || !isCageToolActive}
          />
        </div>
      </div>

      <div className="AlignPopupParameterSelector-button-wrapper">
        <Button
          value={isConfirming ? "Confirming..." : "Confirm"}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        />
      </div>
    </div>
  );
};

export default Cage;
